import React, { useState, useRef, useEffect } from "react";

// CSS
import "./gallery.css";

// Components
import AddFlow from "./AddFlow";
import ImageView from "./ImageView";

// Helpers
import { createAlbum, fetchAlbums } from "./api/albumApi";
import { fetchImages, deleteImage } from "./api/imageApi";

// Images
import albumIcon from "./picture/album.png";
import plusIcon from "./picture/Plus.png";
import addAlbumIcon from "./picture/addAlbum.png";
import leftArrow from "./picture/leftArrow.png";
import rightArrow from "./picture/rightArrow.png";
import backImg from "./picture/back.png";
import { useGlobalContext } from "../Context/Context";

const Gallery = () => {
  const [items, setItems] = useState([]); // Combined items (albums + images)
  const [albums, setAlbums] = useState([]); // Album-specific state
  const [currentPage, setCurrentPage] = useState(1);
  const [addingImage, setAddingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [openAlbum, setOpenAlbum] = useState(null);
  const [error, setError] = useState(null);

  const { galleryUrl } = useGlobalContext();

  const fileInputRef = useRef(null);

  const itemsPerPageFirst = 5;
  const itemsPerPageOther = 7;

  let userId = 1;

  useEffect(() => {
    const adjustTextWrapping = () => {
      const textElements = document.querySelectorAll(".image-gallery p");

      textElements.forEach((pTag) => {
        if (pTag.scrollWidth > pTag.clientWidth) {
          pTag.style.whiteSpace = "normal"; // Allow wrapping
          pTag.style.wordBreak = "break-word"; // Ensure long words break
        }
      });
    };

    adjustTextWrapping();

    // Optional: Run this function whenever the window resizes to adjust wrapping dynamically
    window.addEventListener("resize", adjustTextWrapping);
    return () => window.removeEventListener("resize", adjustTextWrapping);
  }, []); // Runs once when the component mounts

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const [albums, images] = await Promise.all([
          fetchAlbums(userId),
          fetchImages(userId),
        ]);

        const formattedAlbums = albums.map((album) => ({
          ...album,
          images: album.images || [],
          type: "album",
        }));

        const formattedImages = images.map((image) => ({
          ...image,
          type: "image",
          album_id: image.album_id === "0" ? null : Number(image.album_id), // Normalize `album_id`
        }));

        setItems([...formattedAlbums, ...formattedImages]);
        console.log("Albums:", formattedAlbums);
        console.log("Images:", formattedImages);
      } catch (err) {
        console.error("Failed to load gallery content:", err);
        setError("Failed to load gallery content.");
      }
    };

    loadGallery();
  }, []);

  const handleUpdateImage = (id, newTitle, newDescription) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? { ...item, title: newTitle, description: newDescription }
          : item
      )
    );
  };

  const handleDeleteImage = async (imageId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this image?"
    );
    if (!confirmDelete) return;

    try {
      await deleteImage(imageId); // Call the API to delete the image
      setItems((prevItems) =>
        prevItems.filter((item) => item.id !== imageId || item.type !== "image")
      ); // Remove from frontend state
      alert("Image deleted successfully!");
    } catch (error) {
      alert("Failed to delete image. Please try again.");
    }
  };

  // Handle adding a new album
  const handleAddAlbum = async () => {
    const albumName = prompt("Enter the album name:");
    if (!albumName) return;

    try {
      const newAlbum = await createAlbum(1, albumName.trim()); // Replace with dynamic user ID
      if (newAlbum) {
        const addedAlbum = {
          id: newAlbum.album_id,
          type: "album",
          name: newAlbum.name,
          count: 0,
          images: [],
        };
        setAlbums((prev) => [...prev, addedAlbum]);
        setItems((prevItems) => [...prevItems, addedAlbum]);
      } else {
        alert("Failed to create album.");
      }
    } catch {
      alert("Error creating album.");
    }
  };

  const handleAddImage = (event) => {
    const file = event.target.files[0];
    if (file) {
      const objectURL = URL.createObjectURL(file); // Create a temporary preview URL
      setSelectedImage({ file, preview: objectURL }); // Pass both file and preview
      setAddingImage(true); // Show AddFlow
    }
  };

  const handleImageConfirm = (newImage) => {
    setItems((prevItems) => [
      ...prevItems,
      { ...newImage, id: prevItems.length + 1, type: "image" },
    ]);
    setAddingImage(false);
  };

  const handleNextPage = () => setCurrentPage((prev) => prev + 1);
  const handlePrevPage = () => setCurrentPage((prev) => prev - 1);

  const handleOpenAlbum = (album) => {
    setOpenAlbum(album); // Set the selected album
    setCurrentPage(1); // Reset pagination
  };

  const renderItems = () => {
    console.log("Rendering items, current items:", items);

    const data = openAlbum
      ? items.filter(
          (item) =>
            item.type === "image" &&
            Number(item.album_id) === Number(openAlbum.id) // Match images in the current album
        )
      : items.filter(
          (item) =>
            item.type === "album" ||
            (item.type === "image" && (!item.album_id || item.album_id === "0")) // Images not in albums
        );

    console.log("Filtered data for rendering:", data);

    const itemsPerPage =
      currentPage === 1 ? itemsPerPageFirst : itemsPerPageOther;
    const startIdx =
      currentPage === 1
        ? 0
        : itemsPerPageFirst + (currentPage - 2) * itemsPerPageOther;
    const endIdx = startIdx + itemsPerPage;
    const displayedItems = data.slice(startIdx, endIdx);

    console.log("Displayed items after pagination:", displayedItems);

    const gridItems = [];

    if (!openAlbum && currentPage === 1) {
      gridItems.push(
        <div key="add-image" className="grid-item">
          <button
            className="add-image-btn"
            onClick={() => fileInputRef.current.click()}
          >
            <img src={plusIcon} alt="Add Image" />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleAddImage}
          />
        </div>
      );
    }

    displayedItems.forEach((item) => {
      if (item.type === "album" && !openAlbum) {
        gridItems.push(
          <div
            key={item.id}
            className="grid-item"
            onClick={() => handleOpenAlbum(item)}
          >
            <div className="album">
              <img src={albumIcon} alt={item.name} />
              <p>{item.name}</p>
              <span>{item.image_count}</span>
            </div>
          </div>
        );
      } else if (item.type === "image") {
        gridItems.push(
          <div
            key={item.image_id}
            className="grid-item"
            onClick={() => setViewingImage(item)}
          >
            <div className="image-gallery">
              <img src={galleryUrl + item.src || albumIcon} alt={item.title} />
              <p>{item.title}</p>
            </div>
          </div>
        );
      }
    });

    if (!openAlbum && currentPage === 1) {
      gridItems.push(
        <div key="add-album" className="grid-item">
          <button className="add-album-btn" onClick={handleAddAlbum}>
            <img src={addAlbumIcon} alt="Add Album" />
          </button>
        </div>
      );
    }

    gridItems.push(
      <div key="pagination" className="grid-item pagination">
        {currentPage > 1 && (
          <div className="left-right-arrow" onClick={handlePrevPage}>
            <img src={leftArrow} alt="Previous Page" />
          </div>
        )}
        {endIdx < data.length && (
          <div className="left-right-arrow" onClick={handleNextPage}>
            <img src={rightArrow} alt="Next Page" />
          </div>
        )}
      </div>
    );

    return gridItems;
  };

  const handleAddToAlbum = (imageId, newAlbumId) => {
    setItems((prevItems) => {
      let imageToMove = null;

      const updatedItems = prevItems.filter((item) => {
        if (item.type === "image" && item.image_id === imageId) {
          imageToMove = { ...item, album_id: newAlbumId };
          return false; // Remove from the current view
        }

        if (
          item.type === "album" &&
          item.images.some((img) => img.image_id === imageId)
        ) {
          item.images = item.images.filter((img) => img.image_id !== imageId);
          return true;
        }

        return true;
      });

      return updatedItems.map((item) =>
        item.type === "album" && item.id === newAlbumId
          ? {
              ...item,
              images: [...item.images, imageToMove],
              count: item.images.length + 1,
            }
          : item
      );
    });
  };

  return (
    <div className="gallery">
      {viewingImage ? (
        <ImageView
          image={viewingImage}
          onClose={() => setViewingImage(null)}
          onDelete={handleDeleteImage}
          onUpdate={handleUpdateImage}
          albums={items.filter((item) => item.type === "album")}
          onAddToAlbum={handleAddToAlbum}
        />
      ) : addingImage ? (
        <AddFlow
          selectedImage={selectedImage}
          onCancel={() => setAddingImage(false)}
          onConfirm={handleImageConfirm}
        />
      ) : (
        <div className="grid">
          {openAlbum && (
            <div className="grid-item">
              <button className="back-btn" onClick={() => setOpenAlbum(null)}>
                <img src={backImg} />
              </button>
            </div>
          )}
          {renderItems()}
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
};

export default Gallery;
